import { useQuery } from '@tanstack/react-query';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { ExportButtons } from '@mlm/ui/components/export-buttons';
import type { ExportTable } from '@mlm/ui/lib/export-table';
import { EXPORT_PERMS } from '@mlm/shared';
import { reportVortexStatus, type VortexStatusResponse } from '../lib/api';
import { useHasPerm } from '../lib/auth-store';

const EXPORT_PERMS_PAIR = { excel: EXPORT_PERMS.excel, pdf: EXPORT_PERMS.pdf };

/** Stamped into each file so a saved export says when it was taken. */
const takenAt = () => `Generated ${new Date().toLocaleString()}`;

/**
 * Queue and Watchlist export as SEPARATE files: `ExportTable` is one table per
 * workbook by construction (a single `sheetName`/`columns`/`rows`), and the two
 * share no columns worth forcing together.
 *
 * The Status badge becomes plain text — a workbook cell has no badge — and the
 * re-entry marker, which the table renders as a suffix on the slot code, gets
 * its own column so it can be filtered on.
 */
function queueTable(data: VortexStatusResponse): ExportTable {
  return {
    title: 'Vortex Queue',
    subtitle: [
      `Token requirement ${data.tokenRequirement} - graduation at ${data.graduationRequirement} entries`,
      takenAt(),
    ],
    sheetName: 'Queue',
    filenameBase: 'vortex-queue',
    columns: [
      { header: '#', key: 'vortexId', type: 'string' },
      { header: 'Slot', key: 'slotCode', type: 'string' },
      { header: 'Re-entry', key: 'afterGraduate', type: 'string' },
      { header: 'Member', key: 'member', type: 'string' },
      { header: 'Email', key: 'email', type: 'string' },
      { header: 'Membership', key: 'membershipName', type: 'string' },
      { header: 'Status', key: 'status', type: 'string' },
      { header: 'Entries remaining', key: 'entriesRemaining', type: 'number' },
      { header: 'Entered', key: 'createdAt', type: 'datetime' },
    ],
    rows: data.queue.map((r) => ({
      vortexId: r.vortexId,
      slotCode: r.slotCode,
      afterGraduate: r.afterGraduate ? 'Yes' : 'No',
      member: r.fullName ?? r.email,
      email: r.email,
      membershipName: r.membershipName,
      status: r.graduated
        ? 'Graduated'
        : r.entriesRemaining === 0
          ? 'Eligible'
          : `Waiting - needs ${r.entriesRemaining} more`,
      entriesRemaining: r.entriesRemaining,
      createdAt: new Date(r.createdAt),
    })),
  };
}

function watchlistTable(data: VortexStatusResponse): ExportTable {
  return {
    title: 'Vortex Watchlist',
    subtitle: [`Token requirement ${data.tokenRequirement}`, takenAt()],
    sheetName: 'Watchlist',
    filenameBase: 'vortex-watchlist',
    columns: [
      { header: 'Slot', key: 'slotCode', type: 'string' },
      { header: 'Member', key: 'member', type: 'string' },
      { header: 'Email', key: 'email', type: 'string' },
      { header: 'Membership', key: 'membershipName', type: 'string' },
      { header: 'Tokens', key: 'availableToken', type: 'money' },
      { header: 'Lifetime tokens', key: 'lifetimeTokenEarned', type: 'money' },
      { header: 'Progress %', key: 'progressPct', type: 'number' },
    ],
    rows: data.watchlist.map((r) => ({
      slotCode: r.slotCode,
      member: r.fullName ?? r.email,
      email: r.email,
      membershipName: r.membershipName,
      // Numeric strings straight through: the writer hands them to Excel as
      // real numbers without a lossy Number() round-trip.
      availableToken: r.availableToken,
      lifetimeTokenEarned: r.lifetimeTokenEarned,
      progressPct: r.progressPct,
    })),
  };
}

/**
 * Vortex Monitor — read-only view of "who's qualified for vortex". Two sections:
 *   - Queue: every vortex_slot entry, its position, and whether it has graduated
 *     or how many more entries must be minted before it does.
 *   - Watchlist: slots accumulating tokens toward the entry requirement.
 *
 * Modeled on reports.tsx. Pure reads — no mutations.
 */
export function VortexPage() {
  const q = useQuery({ queryKey: ['vortex-status'], queryFn: reportVortexStatus });

  const data = q.data;
  const graduatedCount = data?.queue.filter((r) => r.graduated).length ?? 0;
  const waitingCount = (data?.queue.length ?? 0) - graduatedCount;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Vortex Monitor</h1>
        <p className="text-sm text-muted-foreground">
          The Vortex queue (who has qualified) and slots accumulating tokens toward entry.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Token requirement" value={data ? fmt(data.tokenRequirement) : '—'} />
        <Stat label="Graduation N" value={data ? String(data.graduationRequirement) : '—'} />
        <Stat label="Total minted" value={data ? String(data.totalMinted) : '—'} />
        <Stat label="Graduated" value={String(graduatedCount)} />
        <Stat label="Waiting" value={String(waitingCount)} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Queue — qualified entries</CardTitle>
          <ExportButtons
            // A THUNK: the rows are snapshotted at click time, not at render.
            table={() => (data ? queueTable(data) : null)}
            disabled={!data?.queue.length}
            perms={EXPORT_PERMS_PAIR}
            useHasPerm={useHasPerm}
          />
        </CardHeader>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !data?.queue.length ? (
            <p className="p-6 text-sm text-muted-foreground">No vortex entries yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium text-right">#</th>
                  <th className="px-4 py-2 font-medium">Slot</th>
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Membership</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Entered</th>
                </tr>
              </thead>
              <tbody>
                {data.queue.map((r) => (
                  <tr key={r.vortexId} className="border-b last:border-0">
                    <td className="px-4 py-2 text-right font-mono text-xs">{r.vortexId}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.slotCode}
                      {r.afterGraduate && (
                        <span className="ml-1 text-[10px] text-muted-foreground">(re-entry)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.fullName ?? r.email}
                      <span className="text-muted-foreground"> ({r.email})</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{r.membershipName}</td>
                    <td className="px-4 py-2 text-xs">
                      {r.graduated ? (
                        <Badge variant="success">Graduated</Badge>
                      ) : r.entriesRemaining === 0 ? (
                        <Badge>Eligible</Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          Waiting · needs {r.entriesRemaining} more
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Watchlist — accumulating tokens</CardTitle>
          <ExportButtons
            table={() => (data ? watchlistTable(data) : null)}
            disabled={!data?.watchlist.length}
            perms={EXPORT_PERMS_PAIR}
            useHasPerm={useHasPerm}
          />
        </CardHeader>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !data?.watchlist.length ? (
            <p className="p-6 text-sm text-muted-foreground">No slots are holding vortex tokens.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Slot</th>
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Membership</th>
                  <th className="px-4 py-2 font-medium text-right">Tokens</th>
                  <th className="px-4 py-2 font-medium">Progress to entry</th>
                </tr>
              </thead>
              <tbody>
                {data.watchlist.map((r) => (
                  <tr key={r.slotId} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{r.slotCode}</td>
                    <td className="px-4 py-2 text-xs">
                      {r.fullName ?? r.email}
                      <span className="text-muted-foreground"> ({r.email})</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{r.membershipName}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {fmt(r.availableToken)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${r.progressPct}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-muted-foreground">{r.progressPct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-lg font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** Trim trailing zeros from a money/numeric string for compact display. */
function fmt(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 4 }) : v;
}
